import type {
  EventDayDto,
  EventDetailDto,
  EventListItemDto,
  EventOccurrenceDto,
} from '@/services/platform/api/model';
import { randomUuid } from '@/lib/core/utils';
import { dateKeyRange, toDateKey } from '@/services/platform/data/events';
import { isMembersOnly } from '@/components/shared/events/list/visibility';
import {
  campaignServerDays,
  isCardSoldOut,
  isSoldOut,
  resolveBookingScope,
  resolveGuestLimit,
} from '@/components/shared/events/rules';
import type { BookingState } from './types';

/** Matches an ISO date-time with no timezone designator (naive, Europe/Lisbon wall-clock). */
const NAIVE_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

/** `HH:mm` (Europe/Lisbon wall-clock) for an occurrence's `startDateTime`, or `null` for an all-day event. */
function parseTime(isoDateTime?: string | null): string | null {
  if (!isoDateTime) return null;
  if (NAIVE_DATETIME.test(isoDateTime)) return isoDateTime.slice(11, 16);
  const parsed = new Date(isoDateTime);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Europe/Lisbon',
  }).format(parsed);
}

/** Local-midnight `Date` for a `YYYY-MM-DD` day key — comparable to `Calendar`'s day-key matcher. */
export function dateKeyToLocalDate(dateKey: string): Date {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export interface ResolvedOccurrence {
  id?: string;
  date: Date;
  /** `HH:mm`, or `null` for an all-day occurrence. */
  time: string | null;
  venueNodeKey?: string | null;
  venueName?: string | null;
}

/**
 * The `YYYY-MM-DD` days an occurrence covers — its start day, or, for one spanning several days (a
 * themed week held as a single 20 → 24 Oct occurrence), every day from start to end by the same
 * range rule the Events Calendar buckets by (`dateKeyRange`). A span is floored at today
 * (Europe/Lisbon): an ongoing occurrence keeps its original start, and the days behind it are gone.
 */
function occurrenceDayKeys(occurrence: Pick<EventOccurrenceDto, 'startDateTime' | 'endDateTime'>): string[] {
  const keys = dateKeyRange(occurrence.startDateTime, occurrence.endDateTime);
  if (keys.length <= 1) return keys;
  const todayKey = toDateKey(new Date().toISOString());
  return todayKey ? keys.filter(key => key >= todayKey) : keys;
}

/** Whether `date` is one of the days an occurrence covers (see `occurrenceDayKeys`). */
function occurrenceCoversDay(
  occurrence: Pick<EventOccurrenceDto, 'startDateTime' | 'endDateTime'>,
  date: Date
): boolean {
  const target = date.toDateString();
  return occurrenceDayKeys(occurrence).some(key => dateKeyToLocalDate(key).toDateString() === target);
}

/**
 * The days an occurrence is offered on. A `SevenRooms` reservation is for one day and time, so an
 * occurrence spanning several days is offered on each of them; a `Salesforce` (or any other)
 * registration is for the occurrence as a whole, so it keeps its start day only.
 */
function bookableDayKeys(
  event: EventDetailDto,
  occurrence: Pick<EventOccurrenceDto, 'startDateTime' | 'endDateTime'>
): string[] {
  if (event.bookingMode === 'SevenRooms') return occurrenceDayKeys(occurrence);
  const startKey = toDateKey(occurrence.startDateTime);
  return startKey ? [startKey] : [];
}

/**
 * A `SevenRooms` campaign's bookable days — its server `days[]` (see `campaignServerDays`), or
 * `null` for any other event, which keeps reading `occurrences[]`. A derived day has no
 * `occurrenceId`, and SevenRooms books it by date and time alone. A campaign in another booking
 * mode is left out: its registration needs an `occurrenceId` a derived day lacks.
 */
function campaignDays(event: EventDetailDto): EventDayDto[] | null {
  return event.bookingMode === 'SevenRooms' ? campaignServerDays(event) : null;
}

/** The campaign day (see `campaignDays`) falling on `date`, if any. */
function findCampaignDay(days: EventDayDto[], date: Date): EventDayDto | undefined {
  const target = date.toDateString();
  return days.find(
    serverDay => serverDay.date && dateKeyToLocalDate(serverDay.date).toDateString() === target
  );
}

/** Identity key for matching/grouping by venue — the node key when present, else the name. */
function venueKeyOf(venueNodeKey?: string | null, venueName?: string | null): string | null {
  return venueNodeKey ?? venueName ?? null;
}

/**
 * Normalises an event's bookable instances. `occurrences[]` (contract v1.12) is the
 * authoritative source for multi-date events; a plain one-off event has none, so a single
 * occurrence is synthesised from the top-level schedule/venue fields. A SevenRooms campaign reads
 * the server's `days[]` instead (see `campaignDays`) — a day backed by an occurrence takes its id
 * and venue, a day derived from the recurrence the event's.
 *
 * `venueKey` (see `venueKeyOf`) narrows the result to one venue's occurrences — needed
 * once the user has picked a venue on a multi-venue event (see `resolveVenueOptions`),
 * otherwise dates from every venue would be offered together.
 */
export function resolveOccurrences(event: EventDetailDto, venueKey?: string | null): ResolvedOccurrence[] {
  let resolved: ResolvedOccurrence[];
  const days = campaignDays(event);

  if (days) {
    resolved = days.flatMap((serverDay): ResolvedOccurrence[] => {
      if (!serverDay.date) return [];
      const backing = serverDay.occurrenceId
        ? event.occurrences?.find(occurrence => occurrence.id === serverDay.occurrenceId)
        : undefined;
      const allDay = backing?.allDay ?? event.allDay;
      return [
        {
          id: backing?.id,
          date: dateKeyToLocalDate(serverDay.date),
          time: allDay ? null : parseTime(serverDay.startDateTime),
          venueNodeKey: backing?.venueNodeKey ?? event.venueNodeKey,
          venueName: backing?.venueName ?? event.venueName,
        },
      ];
    });
  } else if (event.occurrences?.length) {
    // One entry per day an occurrence is offered on (see `bookableDayKeys`) — a multi-day SevenRooms
    // occurrence yields several, all with its `id`.
    resolved = event.occurrences.flatMap((occurrence: EventOccurrenceDto): ResolvedOccurrence[] => {
      // Per-occurrence `allDay` (v1.12) — an event's occurrences can mix all-day and timed
      // dates; the event-level flag is only a fallback for occurrences that omit their own.
      const allDay = occurrence.allDay ?? event.allDay;
      return bookableDayKeys(event, occurrence).map(dateKey => ({
        id: occurrence.id,
        date: dateKeyToLocalDate(dateKey),
        time: allDay ? null : parseTime(occurrence.startDateTime),
        venueNodeKey: occurrence.venueNodeKey ?? event.venueNodeKey,
        venueName: occurrence.venueName ?? event.venueName,
      }));
    });
  } else {
    resolved = bookableDayKeys(event, event).map(dateKey => ({
      date: dateKeyToLocalDate(dateKey),
      time: event.allDay ? null : parseTime(event.startDateTime),
      venueNodeKey: event.venueNodeKey,
      venueName: event.venueName,
    }));
  }

  return venueKey == null
    ? resolved
    : resolved.filter(occurrence => venueKeyOf(occurrence.venueNodeKey, occurrence.venueName) === venueKey);
}

/**
 * The event's venue — the first occurrence's, falling back to the top-level fields.
 * Only meaningful for single-venue events; a multi-venue occurrence set needs the
 * user's chosen venue instead (see `resolveVenueOptions`).
 *
 * Reads `event.occurrences` directly rather than going through `resolveOccurrences` —
 * venue and date are independent fields, so an occurrence whose `startDateTime` fails
 * to parse (and gets dropped by `resolveOccurrences`) must not also blank out the venue.
 */
export function resolveVenue(event: EventDetailDto): {
  venueNodeKey?: string | null;
  venueName?: string | null;
} {
  const first = event.occurrences?.[0];
  return {
    venueNodeKey: first?.venueNodeKey ?? event.venueNodeKey,
    venueName: first?.venueName ?? event.venueName,
  };
}

/**
 * Distinct venues across the event's occurrences, in first-seen order — the choices
 * offered on the "Where" step for a multi-venue event. A single-venue (or one-off)
 * event yields exactly one option, matching `resolveVenue`. Reads `event.occurrences`
 * directly (not `resolveOccurrences`) for the same reason `resolveVenue` does — an
 * occurrence with an unparseable date must not also drop its venue from the choices.
 */
export function resolveVenueOptions(
  event: EventDetailDto
): { venueNodeKey?: string | null; venueName?: string | null }[] {
  const venues = event.occurrences?.length
    ? event.occurrences.map(o => ({
        venueNodeKey: o.venueNodeKey ?? event.venueNodeKey,
        venueName: o.venueName ?? event.venueName,
      }))
    : [resolveVenue(event)];

  const seen = new Set<string>();
  const options: { venueNodeKey?: string | null; venueName?: string | null }[] = [];
  for (const venue of venues) {
    const key = venueKeyOf(venue.venueNodeKey, venue.venueName);
    if (key === null || seen.has(key)) continue;
    seen.add(key);
    options.push(venue);
  }
  return options;
}

export type EventWhereOptionKind = 'occurrence' | 'groupEvent';

/**
 * One row of the "Where" step — either one of this event's own occurrence venues, or a sibling
 * event of the same group (contract v1.12 §3.0: a group is several full editions, each with its
 * own venue/menu/price/description — not just a venue variant of the same event).
 */
export interface EventWhereOption {
  key: string;
  kind: EventWhereOptionKind;
  eventId?: string;
  venueNodeKey?: string | null;
  venueName?: string | null;
  /** Secondary line for a `groupEvent` row — the sibling's title, when it differs from the
   * current event's (a plain venue variant of the very same edition needs no extra label). */
  detail?: string;
  disabled?: boolean;
}

/** Identity key for a "Where" row — an event id + venue pair, since two editions of a group can
 * legitimately share a venue (unlike `venueKeyOf`, which alone would collide them). */
export function whereOptionKey(
  eventId: string | undefined,
  venueNodeKey?: string | null,
  venueName?: string | null
): string {
  return `${eventId ?? ''}|${venueKeyOf(venueNodeKey, venueName) ?? ''}`;
}

/**
 * "Where" options for a group-aware event: this event's own occurrence venues (see
 * `resolveVenueOptions`) plus one row per sibling event of the same group, deduped by
 * `whereOptionKey`. `groupEvents` is the group's OTHER events (the current one excluded) —
 * see `useEventGroupEvents`.
 */
export function buildWhereOptions(
  event: EventDetailDto,
  groupEvents: EventListItemDto[]
): EventWhereOption[] {
  const options: EventWhereOption[] = resolveVenueOptions(event).map(option => ({
    key: whereOptionKey(event.id, option.venueNodeKey, option.venueName),
    kind: 'occurrence',
    eventId: event.id,
    venueNodeKey: option.venueNodeKey,
    venueName: option.venueName,
  }));

  const seen = new Set(options.map(option => option.key));
  for (const sibling of groupEvents) {
    if (!sibling.id) continue;
    const key = whereOptionKey(sibling.id, sibling.venueNodeKey, sibling.venueName);
    if (seen.has(key)) continue;
    seen.add(key);
    options.push({
      key,
      kind: 'groupEvent',
      eventId: sibling.id,
      venueNodeKey: sibling.venueNodeKey,
      venueName: sibling.venueName,
      detail: sibling.title && sibling.title !== event.title ? sibling.title : undefined,
      disabled: isCardSoldOut(sibling),
    });
  }

  return options;
}

export type WhenMode = 'locked' | 'dateOnly' | 'normal';

/**
 * `locked`: a single instance with nothing left to pick — date+time are pre-filled and not
 * editable (a one-off all-day event, or a fixed-schedule non-SevenRooms event such as a
 * Salesforce-mode JNcQUOI Club booking, whose occurrence's own time is already final).
 * `dateOnly`: several dated instances but no time-of-day (`allDay`) — the user picks
 * a day from the whitelist only, time is not shown.
 * `normal`: several dated instances with a time-of-day, or a single SevenRooms occurrence
 * with a time — the user picks a whitelisted day (skipped when there's only one, see
 * `WhenSheet`'s `isSingleDay`), then a time via the normal SevenRooms availability flow.
 */
export function resolveWhenMode(event: EventDetailDto, venueKey?: string | null): WhenMode {
  // Counted over the dates still bookable: when only one is left, it is pre-filled like any
  // single-date event rather than offered as a "choice" among sold-out ones.
  const occurrences = resolveSelectableOccurrences(event, venueKey);
  // A single SevenRooms occurrence still needs a room/time pick even though there's only
  // one day — only a genuinely fixed (non-SevenRooms, or all-day) single instance is locked.
  const isTimedSevenRooms = event.bookingMode === 'SevenRooms' && occurrences.some(o => o.time !== null);
  if (occurrences.length <= 1 && !isTimedSevenRooms) return 'locked';
  // Per-occurrence `allDay` (already resolved into `time` by `resolveOccurrences`) — a mixed
  // set of all-day and timed dates behaves as `normal`, with the all-day ones showing no time.
  return occurrences.every(occurrence => occurrence.time === null) ? 'dateOnly' : 'normal';
}

/** The single date+time to pre-fill when `resolveWhenMode` is `'locked'` — the bookable one. */
export function resolveLockedWhen(
  event: EventDetailDto,
  venueKey?: string | null
): { date: Date; time: string | null } | null {
  const [first] = resolveSelectableOccurrences(event, venueKey);
  return first ? { date: first.date, time: first.time } : null;
}

/** Whether an occurrence (or, with none, the event's own fields) can still be booked — not sold out. */
function isOccurrenceBookable(event: EventDetailDto, occurrence?: EventOccurrenceDto | null): boolean {
  return !isSoldOut(resolveBookingScope(event, occurrence));
}

/**
 * Whether a day chosen outside the flow (the Events Calendar's day the member tapped) can still be
 * booked: the event has an occurrence on it and that occurrence is not sold out. A day that fails
 * this is never swapped for another date silently — the flow says so and lets the member choose.
 */
export function isDateBookable(event: EventDetailDto, date: Date, venueKey?: string | null): boolean {
  // A campaign's day is one the server lists; a derived one has no occurrence, so the event's own
  // status decides (the day's `status` is the event's then).
  const days = campaignDays(event);
  if (days) {
    if (!findCampaignDay(days, date)) return false;
    return isOccurrenceBookable(event, findOccurrenceForDate(event, date, venueKey));
  }
  const occurrence = findOccurrenceForDate(event, date, venueKey);
  if (!occurrence) return false;
  // `findOccurrenceForDate` returns the only occurrence whatever the day; it must cover that day —
  // any day of its span, not just the start (a day tapped mid-way through a themed week). Checked
  // in every booking mode: a Salesforce registration covers the whole span, so the day is not
  // "unavailable" there either, even though it is not offered as a separate date.
  if (!occurrenceCoversDay(occurrence, date)) return false;
  return isOccurrenceBookable(event, occurrence);
}

/**
 * The dates the "When" step offers: the ones still bookable (`isSoldOut` — `Full`, or no seats left
 * where a capacity exists), so the member lands on a date that can actually be booked. When every
 * date is sold out, all of them — the step still shows the event's dates and `book.tsx`'s sold-out
 * gate explains why it cannot continue, instead of an empty calendar.
 */
function resolveSelectableOccurrences(event: EventDetailDto, venueKey?: string | null): ResolvedOccurrence[] {
  const all = resolveOccurrences(event, venueKey);
  const open = all.filter(resolved =>
    isOccurrenceBookable(
      event,
      event.occurrences?.find(occurrence => occurrence.id === resolved.id)
    )
  );
  return open.length > 0 ? open : all;
}

/**
 * A booked occurrence's own `HH:mm` (Europe/Lisbon) — the fixed time of a `Salesforce`-mode
 * (or any non-`SevenRooms`) occurrence, as opposed to a time the user picks via the SevenRooms
 * room/time grid (`booking.time`). `null` for an all-day occurrence or when none is resolved yet.
 */
export function resolveOccurrenceTime(occurrence?: EventOccurrenceDto | null): string | null {
  if (!occurrence || occurrence.allDay) return null;
  return parseTime(occurrence.startDateTime);
}

/** Whitelist of selectable days for the `Calendar`'s `enabledDates` — bookable dates only, deduped by day. */
export function resolveEnabledDates(event: EventDetailDto, venueKey?: string | null): Date[] {
  const seen = new Set<string>();
  const dates: Date[] = [];
  for (const occurrence of resolveSelectableOccurrences(event, venueKey)) {
    const key = occurrence.date.toDateString();
    if (seen.has(key)) continue;
    seen.add(key);
    dates.push(occurrence.date);
  }
  return dates;
}

/**
 * The raw occurrence matching the selected day — falls back to the only occurrence when there's
 * one, or, with none selected yet, to the first one still bookable (else the first) — the "not
 * chosen" default must not be a sold-out date when a later one is open. When a date WAS
 * selected and matches none of the (venue-scoped) occurrences, returns `undefined` rather
 * than guessing occurrence #1 — silently registering the wrong date is worse than a state
 * `applyEventBookingPatch` can recognise as unresolved. Exported for `applyEventBookingPatch`.
 */
export function findOccurrenceForDate(
  event: EventDetailDto,
  date: Date | null,
  venueKey?: string | null
): EventOccurrenceDto | undefined {
  const all = event.occurrences ?? [];
  // Scoped to one venue when given — otherwise two venues sharing a date would match
  // ambiguously (not the case in today's data, but the lookup shouldn't assume it).
  const occurrences =
    venueKey == null
      ? all
      : all.filter(
          o => venueKeyOf(o.venueNodeKey ?? event.venueNodeKey, o.venueName ?? event.venueName) === venueKey
        );
  // A campaign day resolves to its own occurrence or to none — never to the long-past anchor, which
  // would otherwise be "the only occurrence" and scope the SevenRooms grid to another date.
  const days = campaignDays(event);
  if (days && date) {
    const occurrenceId = findCampaignDay(days, date)?.occurrenceId;
    return occurrenceId ? occurrences.find(occurrence => occurrence.id === occurrenceId) : undefined;
  }
  if (occurrences.length === 0) return undefined;
  if (occurrences.length === 1) return occurrences[0];
  if (!date) return occurrences.find(occurrence => isOccurrenceBookable(event, occurrence)) ?? occurrences[0];
  return occurrences.find(occurrence => occurrenceCoversDay(occurrence, date));
}

/**
 * Applies a `BookingState` patch and re-derives `occurrence` from the merged (event, venue,
 * date) — the single place `booking.occurrence` is written, so it can never drift from the
 * fields it's derived from. Also rotates `idempotencyKey` when the resolved occurrence or the
 * guest count actually changes, so a retry with a DIFFERENT payload never replays a stale key
 * (a same-payload retry keeps it — that's what makes the retry idempotent).
 *
 * Skips resolving an occurrence while a multi-venue event has no venue chosen yet — otherwise
 * `findOccurrenceForDate`'s "no date yet" default would silently pick occurrence #1 of an
 * arbitrary venue before the user has answered "Where".
 */
export function applyEventBookingPatch(prev: BookingState, patch: Partial<BookingState>): BookingState {
  const next: BookingState = { ...prev, ...patch };
  const event = next.event;
  const isMultiVenue = event ? resolveVenueOptions(event).length > 1 : false;
  const venueKey = venueKeyOf(next.venue?.venueNodeKey, next.venue?.name);
  const occurrence =
    event && (!isMultiVenue || venueKey !== null)
      ? (findOccurrenceForDate(event, next.date, venueKey) ?? null)
      : null;

  const occurrenceChanged = occurrence?.id !== prev.occurrence?.id;
  const guestCountChanged = 'guestCount' in patch && patch.guestCount !== prev.guestCount;

  return {
    ...next,
    occurrence,
    idempotencyKey: occurrenceChanged || guestCountChanged ? randomUuid() : next.idempotencyKey,
  };
}

/**
 * Full `BookingState` patch for a freshly-selected event — locked venue, guest cap, and the
 * locked date/time when applicable. Shared by `WhichSheet.handleValueChange` (user picks from
 * the list) and the flow container's own `eventId` pre-select (arriving from the Event Detail
 * Page's "Book now", which already knows which event).
 *
 * A multi-venue event (see `resolveVenueOptions`) leaves venue/date/time unset instead —
 * the user must answer the "Where" step first (`deriveVenueSelectionUpdates`), otherwise
 * this would silently lock in whichever venue happens to be occurrence #1.
 */
export function deriveBookingUpdatesFromEvent(event: EventDetailDto): Partial<BookingState> {
  const isMultiVenue = resolveVenueOptions(event).length > 1;

  if (isMultiVenue) {
    // Per-venue guest limits may differ (each occurrence has its own) — unknowable before
    // "Where" is answered, so no guess is pre-filled here (unlike the single-venue branch).
    return {
      event,
      venue: null,
      service: null,
      guestCount: null,
      date: null,
      time: null,
      room: null,
      seatingAreaPublicGuid: undefined,
    };
  }

  const venue = resolveVenue(event);
  const whenMode = resolveWhenMode(event);
  const locked = whenMode === 'locked' ? resolveLockedWhen(event) : null;
  const occurrence = findOccurrenceForDate(event, null, null) ?? null;
  const guestLimit = resolveGuestLimit(resolveBookingScope(event, occurrence));
  return {
    event,
    venue: {
      venueNodeKey: venue.venueNodeKey,
      name: venue.venueName,
      guestsMax: guestLimit,
      membersOnly: isMembersOnly(event.visibility),
    },
    service: null,
    // A cap of 1 leaves no real choice — pre-fill it so "How many" can be locked (see book.tsx).
    guestCount: guestLimit === 1 ? 1 : null,
    date: locked?.date ?? null,
    time: locked?.time ?? null,
    room: null,
    seatingAreaPublicGuid: undefined,
  };
}

/**
 * `BookingState` patch for picking a venue on a multi-venue event's "Where" step —
 * re-derives date/time scoped to just that venue's occurrences (mirrors the single-venue
 * defaulting `deriveBookingUpdatesFromEvent` does up front, just narrowed to one venue).
 */
export function deriveVenueSelectionUpdates(
  event: EventDetailDto,
  option: { venueNodeKey?: string | null; venueName?: string | null }
): Partial<BookingState> {
  const venueKey = venueKeyOf(option.venueNodeKey, option.venueName);
  const whenMode = resolveWhenMode(event, venueKey);
  const locked = whenMode === 'locked' ? resolveLockedWhen(event, venueKey) : null;
  const occurrence = findOccurrenceForDate(event, null, venueKey) ?? null;
  const guestLimit = resolveGuestLimit(resolveBookingScope(event, occurrence));

  return {
    venue: {
      venueNodeKey: option.venueNodeKey,
      name: option.venueName,
      guestsMax: guestLimit,
      membersOnly: isMembersOnly(event.visibility),
    },
    // A cap of 1 leaves no real choice — pre-fill it so "How many" can be locked (see
    // book.tsx), matching what `deriveBookingUpdatesFromEvent` does for the initial pick.
    guestCount: guestLimit === 1 ? 1 : null,
    date: locked?.date ?? null,
    time: locked?.time ?? null,
    room: null,
    seatingAreaPublicGuid: undefined,
  };
}

/**
 * After a "not enough seats" answer (register 409 / checkout 409 `event_sold_out`) with seats still
 * left: "How many" is capped at what remains — never above the occurrence's own party limit — and
 * re-asked rather than silently shrunk, so the member decides who drops out. The fresh idempotency
 * key makes the retry a new request, not a replay of the rejected one.
 */
export function capGuestsToSeatsLeft(prev: BookingState, seatsLeft: number): BookingState {
  const partyLimit = prev.event
    ? resolveGuestLimit(resolveBookingScope(prev.event, prev.occurrence))
    : undefined;
  const cap = Math.max(1, partyLimit != null ? Math.min(seatsLeft, partyLimit) : seatsLeft);
  return applyEventBookingPatch(prev, {
    venue: prev.venue ? { ...prev.venue, guestsMax: cap } : prev.venue,
    guestCount: cap === 1 ? 1 : null,
    idempotencyKey: randomUuid(),
  });
}

/**
 * Puts a previously chosen occurrence back on a freshly derived booking — returning from checkout,
 * the flow re-reads the event and would otherwise land on its defaults. A multi-venue event first
 * gets that occurrence's venue (the "Where" answer), then its date. Unknown id → unchanged.
 */
export function restoreOccurrence(
  prev: BookingState,
  event: EventDetailDto,
  occurrenceId?: string | null
): BookingState {
  const resolved = occurrenceId
    ? resolveOccurrences(event).find(item => item.id === occurrenceId)
    : undefined;
  if (!resolved) return prev;
  const venuePatch = prev.venue ? {} : deriveVenueSelectionUpdates(event, resolved);
  return applyEventBookingPatch(prev, { ...venuePatch, date: resolved.date });
}
